import { ExcelNumber } from 'components/xlsx/excelCells';
import { generateStyledXLSXFromRows } from 'components/xlsx/xlsxStyled';
import { format as formatDate, isValid, parse } from 'date-fns';
import { saveAs } from 'file-saver';

export type TableFileFormat = 'xlsx' | 'csv';

/** The format as a reader names it: product names, the same in every language. */
export const FORMAT_NAMES: Record<TableFileFormat, string> = { xlsx: 'Excel', csv: 'CSV' };

export interface TableCell {
    text: string;
    /** The absolute URL of the cell's first link: a spreadsheet cell holds one. */
    href?: string;
}

export interface ChatTable<Cell = TableCell> {
    headers: string[];
    rows: Cell[][];
    /** The italic line under a list cut by a limit, which says so. */
    note: string | null;
}

type Decimal = '.' | ',';

interface ParsedNumber {
    /** The number as shown: 12 for "12%". */
    value: number;
    decimals: number;
    grouped: boolean;
    /** A currency written before the number, with the space after it. */
    prefix: string;
    /** A percent sign, hours or a currency written after the number, with the space before it. */
    unit: string;
}

export type TypedCell =
    | ({ type: 'text' } & TableCell)
    | { type: 'date'; text: string; date: Date }
    | ({ type: 'number'; text: string } & ParsedNumber);

const textOf = (element: Element): string => (element.textContent ?? '').replace(/\s+/g, ' ').trim();

const BLOCKS = 'p,h1,h2,h3,h4,h5,h6,ul,ol,pre,blockquote,table,hr';

/** The table's text block's blocks, in document order. */
const blocksAround = (table: HTMLTableElement, selector: string): Element[] =>
    Array.from(table.closest('.markdown-prose')?.querySelectorAll(selector) ?? []);

/** The line right under the table when it is italic as a whole: a list cut by a limit says so there. */
export const noteAfter = (table: HTMLTableElement): string | null => {
    const blocks = blocksAround(table, BLOCKS);
    const next = blocks[blocks.indexOf(table) + 1];
    const emphasis = next?.tagName === 'P' && next.children.length === 1 ? next.firstElementChild : null;
    return emphasis?.tagName === 'EM' && textOf(emphasis) === textOf(next) ? textOf(next) : null;
};

/** The table as the reader sees it: the rendered header and body rows, in document order. */
export const readTable = (table: HTMLTableElement): ChatTable => ({
    headers: Array.from(table.tHead?.rows[0]?.cells ?? [], textOf),
    rows: Array.from(table.tBodies[0]?.rows ?? [], (row) =>
        Array.from(row.cells, (cell): TableCell => {
            // The DOM property, not the attribute: it is the link resolved against the page's origin. Only a
            // web address goes into the file, so a phone or mail link keeps its text alone.
            const href = Array.from(cell.querySelectorAll<HTMLAnchorElement>('a[href]'), (link) => link.href).find(
                (address) => /^https?:/.test(address)
            );
            return href ? { text: textOf(cell), href } : { text: textOf(cell) };
        })
    ),
    note: noteAfter(table),
});

/** The nearest heading above the table in its text block, unless another table stands between the two. */
export const headingBefore = (table: HTMLTableElement): string => {
    const marks = blocksAround(table, 'h1,h2,h3,h4,h5,h6,table');
    const previous = marks[marks.indexOf(table) - 1];
    return previous && previous.tagName !== 'TABLE' ? textOf(previous) : '';
};

// Excel counts a 29 February 1900 that never was, so it would show an earlier date a day late.
const FIRST_EXCEL_DATE = Date.UTC(1900, 2, 1);

const DATE_FORMATS = ['yyyy-MM-dd', 'dd.MM.yyyy', 'd MMM yyyy', 'MMM d, yyyy'];

// date-fns reads "6.10.2026" under `dd` and "26" under `yyyy`: only a date that prints back as written is one.
const parseDate = (text: string): Date | null => {
    for (const pattern of DATE_FORMATS) {
        const local = parse(text, pattern, new Date(2000, 0, 1));
        if (isValid(local) && formatDate(local, pattern) === text) {
            // UTC midnight: a spreadsheet date is a day count, which a local midnight would offset by hours. Date.UTC
            // reads a year below 100 as 19xx, so the year is checked as written.
            const date = Date.UTC(local.getFullYear(), local.getMonth(), local.getDate());
            return local.getFullYear() >= 1900 && date >= FIRST_EXCEL_DATE ? new Date(date) : null;
        }
    }
    return null;
};

// A currency before or after the number, a percent sign or hours after it, never both sides.
const CURRENCY = String.raw`(?:€|[A-Z]{3})`;

// Three capitals are a currency only when they name one: "INV 1001" and "ABC123" are codes.
const CURRENCIES = new Set(Intl.supportedValuesOf('currency'));

// A minus before or after a currency, digits either plain or in thousands groups under one separator, an
// optional decimal part.
const numberPattern = (decimal: string, group: string) =>
    new RegExp(
        String.raw`^(?<lead>[-\u2212]?)(?<prefix>${CURRENCY}\s?)?(?<sign>[-\u2212]?)(?<int>\d{1,3}(?:(?<sep>[ \u00A0\u202F${group}])\d{3})(?:\k<sep>\d{3})*|\d+)(?:${decimal}(?<decimals>\d+))?(?<unit>\s?(?:%|h|${CURRENCY}))?$`
    );

const NUMBER_PATTERNS: Record<Decimal, RegExp> = {
    '.': numberPattern(String.raw`\.`, ','),
    ',': numberPattern(',', '.'),
};

// Excel keeps 15 significant digits: a longer number would come back changed.
const MAX_DIGITS = 15;

// A month and its year, "10.2026" or "2026.10", which the number patterns read as a decimal.
const YEAR_MONTH = /^(?:(?:[1-9]|1[0-2])[.,](?:19|20)\d{2}|(?:19|20)\d{2}[.,](?:0[1-9]|1[0-2]))$/;

const readNumber = (text: string, decimal: Decimal): ParsedNumber | null => {
    const groups = NUMBER_PATTERNS[decimal].exec(text)?.groups;
    if (!groups) {
        return null;
    }
    const { lead, prefix = '', sign, int, sep, decimals = '', unit = '' } = groups;
    const code = (prefix || unit).trim();
    if ((prefix && unit) || (lead && sign) || (/^[A-Z]{3}$/.test(code) && !CURRENCIES.has(code))) {
        return null;
    }
    const digits = int.replace(/\D/g, '');
    // A leading zero belongs to a code, and a number would drop it.
    if ((digits.length > 1 && digits.startsWith('0')) || digits.length + decimals.length > MAX_DIGITS) {
        return null;
    }
    return {
        value: Number(`${lead || sign ? '-' : ''}${digits}.${decimals || '0'}`),
        decimals: decimals.length,
        grouped: sep !== undefined,
        prefix,
        unit,
    };
};

/**
 * The number a cell reads as on its own, and the decimal separator it shows when only one reading fits.
 * `null` when the cell needs its column to say: "1,234" is 1234 or 1.234, whole numbers in groups with
 * nothing after them may be phone numbers, "10.2026" may be a month.
 */
const settledNumber = (text: string): (ParsedNumber & { decimal?: Decimal }) | null => {
    const dot = readNumber(text, '.');
    const comma = readNumber(text, ',');
    if (dot && comma && dot.value !== comma.value) {
        return null;
    }
    const reading =
        dot && comma ? dot : dot ? { ...dot, decimal: '.' as const } : comma && { ...comma, decimal: ',' as const };
    if (!reading || YEAR_MONTH.test(text)) {
        return null;
    }
    return reading.grouped && !reading.decimals && !reading.prefix && !reading.unit ? null : reading;
};

/**
 * A cell typed by its own text: a date in one of the four forms answers write, a number, or text. `decimal`
 * is the separator the rest of its column shows, which only settles a cell that cannot say for itself.
 * A wrong number in a spreadsheet is worse than a text cell, so whatever stays in doubt is text.
 */
export const parseCell = (text: string, decimal?: Decimal): TypedCell => {
    const date = parseDate(text);
    if (date) {
        return { type: 'date', text, date };
    }
    const number = settledNumber(text) ?? (decimal ? readNumber(text, decimal) : null);
    if (!number) {
        return { type: 'text', text };
    }
    const { value, decimals, grouped, prefix, unit } = number;
    return { type: 'number', text, value, decimals, grouped, prefix, unit };
};

/** Types each cell on its own; a linked cell stays text, as a link. */
export const typeTable = ({ headers, rows, note }: ChatTable): ChatTable<TypedCell> => {
    // The decimal separator the column's cells show where they say, when they all say the same one.
    const decimals = headers.map((_, column) => {
        const shown = new Set(rows.flatMap((row) => settledNumber(row[column]?.text ?? '')?.decimal ?? []));
        return shown.size === 1 ? [...shown][0] : undefined;
    });
    return {
        headers,
        rows: rows.map((row) =>
            row.map(
                (cell, column): TypedCell =>
                    cell.href ? { type: 'text', ...cell } : parseCell(cell.text, decimals[column])
            )
        ),
        note,
    };
};

const isPercent = ({ unit }: ParsedNumber) => unit.trim() === '%';

// The number as it was shown: its decimals, its thousands grouping, its currency or unit as literal text.
const numberFormat = (number: ParsedNumber): string => {
    const { decimals, grouped, prefix, unit } = number;
    const digits = `${grouped ? '#,##0' : '0'}${decimals > 0 ? `.${'0'.repeat(decimals)}` : ''}`;
    if (isPercent(number)) {
        return `${digits}${unit}`;
    }
    return `${prefix ? `"${prefix}"` : ''}${digits}${unit ? `"${unit}"` : ''}`;
};

// A date as the day count a spreadsheet stores, from its epoch of 30 December 1899.
const excelSerial = (date: Date) => date.getTime() / 86_400_000 + 25_569;

const xlsxValue = (cell: TypedCell): unknown => {
    switch (cell.type) {
        case 'date':
            return new ExcelNumber(excelSerial(cell.date), 'dd.mm.yyyy');
        case 'number':
            return new ExcelNumber(
                isPercent(cell) ? cell.value / 100 : cell.value,
                numberFormat(cell),
                cell.text.length
            );
        default:
            return cell.href ? { text: cell.text, hyperlink: cell.href } : cell.text || null;
    }
};

const quoted = (field: string): string => (/[;"\r\n]/.test(field) ? `"${field.replaceAll('"', '""')}"` : field);

// A spreadsheet runs a text field that starts like a formula, and the cells hold text users wrote. Only a lone
// plus or minus stays as written: nothing follows the sign. Behind a sign even a phone number is read as a
// number or a subtraction.
const FORMULA_START = /^(?:[=@\t\r]|[+-](?!$))/;

const csvText = (text: string): string => quoted(FORMULA_START.test(text) ? `'${text}` : text);

/** A number as the CSV writes it: a decimal comma and no thousands separator, its currency or unit as text. */
export const csvNumber = (cell: Extract<TypedCell, { type: 'number' }>): string =>
    // Where a semicolon separates the fields, "7.5" opens as a date.
    `${cell.prefix}${cell.value.toFixed(cell.decimals).replace('.', ',')}${cell.unit}`;

const csvField = (cell: TypedCell): string => {
    if (cell.type === 'date') {
        return cell.date.toISOString().slice(0, 10);
    }
    if (cell.type !== 'number') {
        return csvText(cell.text);
    }
    const field = csvNumber(cell);
    // A bare number is one to a spreadsheet too, and so is a percent. With a currency or a unit the field is text,
    // and takes the text guard: after a minus, a spreadsheet reads "-1250,5 EUR" as a formula that names EUR.
    return isPercent(cell) || (!cell.prefix && !cell.unit) ? field : csvText(field);
};

/**
 * The table as semicolon-separated text behind a UTF-8 byte order mark, which is what Excel needs to open
 * it in columns, with its letters intact, in the locales that write a decimal comma.
 */
export const buildTableCsv = ({ headers, rows, note }: ChatTable<TypedCell>): string => {
    const lines = [
        headers.map(csvText),
        ...rows.map((row) => row.map(csvField)),
        ...(note === null ? [] : [[csvText(note)]]),
    ];
    return `\uFEFF${lines.map((line) => `${line.join(';')}\r\n`).join('')}`;
};

const MAX_FILE_NAME_LENGTH = 120;

// Without the characters Windows refuses in a file name, and without control characters.
const cleanName = (part: string): string =>
    part
        .replace(/[\\/:*?"<>|]|(?!\s)\p{Cc}/gu, '')
        .replace(/\s+/g, ' ')
        .trim();

/** `<title> - <heading>`, the heading left out when it repeats the title. */
const fileStem = (title: string, heading: string, fallbackTitle: string): string => {
    const name = cleanName(title) || fallbackTitle;
    const part = cleanName(heading);
    const stem = part && part !== name ? `${name} - ${part}` : name;
    // By character, not by code unit: a cut through the middle of an emoji would leave half of it.
    return Array.from(stem).slice(0, MAX_FILE_NAME_LENGTH).join('').trim();
};

/**
 * The table's file name: the thread title and the heading above the table, and ` (2)` on the second table of
 * the answer that would take the same name.
 */
export const tableFileName = (
    table: HTMLTableElement,
    title: string,
    fallbackTitle: string,
    format: TableFileFormat
): string => {
    const stemOf = (other: HTMLTableElement) => fileStem(title, headingBefore(other), fallbackTitle);
    const stem = stemOf(table);
    const answer = table.closest('.markdown-prose')?.parentElement;
    // The text blocks' tables only: a chart beside them carries a table for screen readers.
    const tables = answer ? Array.from(answer.querySelectorAll<HTMLTableElement>('.markdown-prose table')) : [table];
    const ordinal = tables.slice(0, tables.indexOf(table)).filter((other) => stemOf(other) === stem).length + 1;
    return `${stem}${ordinal > 1 ? ` (${ordinal})` : ''}.${format}`;
};

/**
 * The heading as a sheet name: 31 code units, none of the characters Excel refuses there, no apostrophe at
 * either end, and never "History", which Excel keeps for itself.
 */
export const sheetName = (heading: string, fallbackTitle: string): string => {
    const name = heading
        .replace(/[[\]:*?/\\]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 31)
        // A cut through the middle of an emoji would leave half of it.
        .replace(/[\uD800-\uDBFF]$/, '')
        .replace(/^[\s']+|[\s']+$/g, '');
    return name && name.toLowerCase() !== 'history' ? name : fallbackTitle;
};

/** The file name of a table with a title of its own: `<thread title> - <table title>`. */
export const titledTableFileName = (
    threadTitle: string,
    tableTitle: string,
    fallbackTitle: string,
    format: TableFileFormat
): string => `${fileStem(threadTitle, tableTitle, fallbackTitle)}.${format}`;

/** Saves a typed table as a file: its cells, typed as they read, its note as the last line. */
export const saveTable = async (
    typed: ChatTable<TypedCell>,
    format: TableFileFormat,
    fileName: string,
    sheet: string
) => {
    if (format === 'csv') {
        saveAs(new Blob([buildTableCsv(typed)], { type: 'text/csv;charset=utf-8' }), fileName);
        return;
    }

    await generateStyledXLSXFromRows(
        typed.headers,
        typed.rows.map((row) => row.map(xlsxValue)),
        fileName,
        { note: typed.note, sheetName: sheet }
    );
};

/** Saves the rendered table as a file: what the reader sees is what the file holds. */
export const downloadTable = (
    table: HTMLTableElement,
    format: TableFileFormat,
    fileName: string,
    fallbackTitle: string
) => saveTable(typeTable(readTable(table)), format, fileName, sheetName(headingBefore(table), fallbackTitle));
